import * as React from 'react';

import { playButtonClickSound } from '@wg/web2clientapi/sound';
import {
  ContextMenu,
  DivTooltip,
  Icon,
  Interpolate,
  ClanName,
  TableBodyCell,
  TableRow,
  Tooltip,
  TooltipBody,
} from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import { REQUEST_STATUSES } from '~/constants';
import { getPositionY } from '~/helpers/contextMenu';
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from '~/helpers/datetime';
import { floats, percent, thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';
import { ROUTES_MAP } from '~/router/routes';

import { Clan, StatisticsValue } from '~/UIKit';

import type { IconType } from '@wg/wows-react-uikit/components/Icon/Icon';
import type { NavigateFunction } from 'react-router-dom';
import type { IMemberApplication } from '~/Actions/ActionApplications';
import type { ClanType } from '~/Actions/ActionInvites';
import type { ICurrentAccountState } from '~/Reducers/ReducerCurrentAccount';

type PropsType = {
  navigate: NavigateFunction;
  application: IMemberApplication;
  currentAccount: ICurrentAccountState;
  hasActiveApplicationForClan: boolean;
  root: string;
  sendApplication: (clan: ClanType) => void;
};

type StateType = {
  isContextMenuActive: boolean;
  contextMenuContext: Array<any>;
  contextMenuPosition: {
    x: number;
    y: number;
  };
};

const getApplicationStatusTooltipContent = (currentAccount: ICurrentAccountState, application: IMemberApplication) => {
  const { status, created_at, expires_at, updated_at } = application;
  const { inClanCooldownTill } = currentAccount;

  if (inClanCooldownTill) {
    return (
      <div>
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Вы отправили заявку на вступление в клан %(date)s&nbsp;в&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(created_at)}
          time={isoToFormattedLocalTime(created_at)}
          parentTag={'p'}
        />
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Вы недавно покинули клан. Вступление в клан станет доступно %(date)s&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(inClanCooldownTill)}
          time={isoToFormattedLocalTime(inClanCooldownTill)}
          parentTag={'p'}
        />
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Время для принятия решения по заявке истекает %(date)s&nbsp;в&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(expires_at)}
          time={isoToFormattedLocalTime(expires_at)}
          parentTag={'p'}
        />
      </div>
    );
  }

  if (status === REQUEST_STATUSES.EXPIRED) {
    return (
      <div>
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Время для принятия решения истекло %(date)s&nbsp;в&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(expires_at)}
          time={isoToFormattedLocalTime(expires_at)}
          parentTag={'p'}
        />
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Вы отправили заявку %(date)s&nbsp;в&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(created_at)}
          time={isoToFormattedLocalTime(created_at)}
          parentTag={'p'}
        />
      </div>
    );
  }

  if (status === REQUEST_STATUSES.ACCEPTED) {
    const { name, tag, color } = application.clan;

    return (
      <div>
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Клан %(clanName)s одобрил заявку %(date)s&nbsp;в&nbsp;%(time)s')}
          clanName={<ClanName clanTag={tag} clanName={name} clanColor={color} />}
          date={isoToFormattedLocalDate(updated_at)}
          time={isoToFormattedLocalTime(updated_at)}
          parentTag={'p'}
        />
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Вы отправили заявку %(date)s&nbsp;в&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(created_at)}
          time={isoToFormattedLocalTime(created_at)}
          parentTag={'p'}
        />
      </div>
    );
  }

  if (status === REQUEST_STATUSES.DECLINED) {
    const { name, tag, color } = application.clan;

    return (
      <div>
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Клан %(clanName)s отклонил заявку %(date)s&nbsp;в&nbsp;%(time)s')}
          clanName={<ClanName clanTag={tag} clanName={name} clanColor={color} />}
          date={isoToFormattedLocalDate(updated_at)}
          time={isoToFormattedLocalTime(updated_at)}
          parentTag={'p'}
        />
        <Interpolate
          useDangerouslySetInnerHTML={true}
          str={t('Вы отправили заявку %(date)s&nbsp;в&nbsp;%(time)s.')}
          date={isoToFormattedLocalDate(created_at)}
          time={isoToFormattedLocalTime(created_at)}
          parentTag={'p'}
        />
      </div>
    );
  }

  return (
    <div>
      <Interpolate
        useDangerouslySetInnerHTML={true}
        str={t('Вы отправили заявку на вступление в клан %(date)s&nbsp;в&nbsp;%(time)s.')}
        date={isoToFormattedLocalDate(created_at)}
        time={isoToFormattedLocalTime(created_at)}
        parentTag={'p'}
      />
      <Interpolate
        useDangerouslySetInnerHTML={true}
        str={t('Время для принятия решения по заявке истекает %(date)s&nbsp;в&nbsp;%(time)s.')}
        date={isoToFormattedLocalDate(expires_at)}
        time={isoToFormattedLocalTime(expires_at)}
      />
    </div>
  );
};

class ApplicationsNonClanMemberTableItem extends React.Component<PropsType, StateType> {
  constructor(props: PropsType) {
    super(props);

    this.state = {
      isContextMenuActive: false,
      contextMenuContext: [],
      contextMenuPosition: { x: 0, y: 0 },
    };
  }

  onContextMenuStateChange = (isContextMenuActive: StateType['isContextMenuActive']) => {
    this.setState({ isContextMenuActive });
  };

  onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const context = this.getContextMenuContext();
    const clientY = getPositionY(event, context);
    this.setState({
      contextMenuContext: context,
      contextMenuPosition: {
        x: event.clientX,
        y: clientY,
      },
    });
  };

  getContextMenuContext = () => {
    const hasActiveApplication = this.props.hasActiveApplicationForClan;
    return [
      {
        name: null,
        items: [
          {
            name: t('Профиль клана'),
            value: 'CLAN_PROFILE',
          },
          {
            name: t('Отправить заявку'),
            disabled: hasActiveApplication,
            tooltip: hasActiveApplication ? t('Заявка уже отправлена') : null,
            value: 'SEND_APPLICATION',
          },
        ],
      },
    ];
  };

  onContextMenuItemClick = (action) => {
    const clan = this.props.application.clan;
    switch (action) {
      case 'CLAN_PROFILE':
        this.props.navigate(`${root}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', String(clan.id))}`);
        break;
      case 'SEND_APPLICATION':
        this.props.sendApplication(clan);
        break;
    }
  };

  onClanClick = () => {
    const clan = this.props.application.clan;
    if (clan.is_active) {
      void playButtonClickSound();
      this.props.navigate(`${root}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', String(clan.id))}`);
    }
  };

  render() {
    const { application, currentAccount } = this.props;

    const isHiddenStatistics = application.is_hidden_statistics;
    const applicationStatusTooltipId = `application-status-tooltip-${application.clan.id}`;
    const applicationStatusTooltip = getApplicationStatusTooltipContent(currentAccount, application);
    const glyph = `status-${application.status}` as IconType;
    const tooltipStatus = applicationStatusTooltip && !this.state.isContextMenuActive && (
      <Tooltip id={applicationStatusTooltipId}>
        <TooltipBody>{applicationStatusTooltip}</TooltipBody>
      </Tooltip>
    );

    const clan = application.clan;
    const clanDisbanded = !clan.is_active;
    const statistics = application.statistics;

    const contextMenu = clan.is_active ? (
      <ContextMenu
        context={this.state.contextMenuContext}
        position={this.state.contextMenuPosition}
        onItemClick={this.onContextMenuItemClick}
        onContextMenuStateChange={this.onContextMenuStateChange}
        isDemo={false}
      />
    ) : null;

    return (
      <TableRow isHovering onContextMenu={clan.is_active ? this.onContextMenu : null} onClick={this.onClanClick}>
        {contextMenu}
        <TableBodyCell modify="basis">
          <Clan
            clanTag={clan.tag}
            clanColor={clan.color}
            clanName={clan.name}
            isDisbanded={clanDisbanded}
            disableTooltip={this.state.isContextMenuActive}
          />
        </TableBodyCell>
        <TableBodyCell modify="small">{clan.members_count}</TableBodyCell>
        <TableBodyCell modify="right">
          <StatisticsValue
            isClan
            clanDisbanded={clanDisbanded}
            value={thousands(statistics.abtl)}
            isHidden={isHiddenStatistics}
            disableTooltip={this.state.isContextMenuActive}
          />
        </TableBodyCell>
        <TableBodyCell modify="right">
          <StatisticsValue
            isClan
            clanDisbanded={clanDisbanded}
            value={percent(statistics.awb, undefined)}
            isHidden={isHiddenStatistics}
            disableTooltip={this.state.isContextMenuActive}
          />
        </TableBodyCell>
        <TableBodyCell modify="right">
          <StatisticsValue
            isClan
            clanDisbanded={clanDisbanded}
            value={thousands(statistics.aeb)}
            isHidden={isHiddenStatistics}
            disableTooltip={this.state.isContextMenuActive}
          />
        </TableBodyCell>
        <TableBodyCell modify="right">
          <StatisticsValue
            isClan
            clanDisbanded={clanDisbanded}
            value={thousands(statistics.admg)}
            isHidden={isHiddenStatistics}
            disableTooltip={this.state.isContextMenuActive}
          />
        </TableBodyCell>
        <TableBodyCell modify="right">
          <StatisticsValue
            isClan
            clanDisbanded={clanDisbanded}
            value={floats(statistics.afb, 2)}
            isHidden={isHiddenStatistics}
            disableTooltip={this.state.isContextMenuActive}
          />
        </TableBodyCell>
        <TableBodyCell modify="right">
          <DivTooltip tooltipBody={tooltipStatus}>
            <Icon glyph={glyph} />
          </DivTooltip>
        </TableBodyCell>
      </TableRow>
    );
  }
}

export default ApplicationsNonClanMemberTableItem;
